import React, { useMemo } from 'react';
import {
  Briefcase,
  MessageCircle,
  CheckCircle,
  Clock,
  Timer,
  Star,
  AlertTriangle,
  ShieldAlert,
  TrendingUp,
  Activity,
  BarChart3,
  Lightbulb,
  Sparkles,
  CheckCircle2,
  XCircle,
  Building2,
  Database,
  Trash2,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useWaExecutivo, ExecutiveTrend } from '../../hooks/whatsapp/useWaExecutivo';
import { parseUTC } from '../../services/whatsapp/waFormatters';

// Paleta no tom Apple/WhatsApp usada nos demais relatórios.
const COLORS = ['#007AFF', '#34C759', '#FF9500', '#FF2D55', '#AF52DE', '#5856D6', '#8E8E93'];
const tooltipStyle = {
  borderRadius: '12px',
  border: 'none',
  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
  fontSize: '13px',
};

// Labels legíveis para prioridade e canal (chaves cruas vêm do backend).
const PRIORITY_LABELS: Record<string, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
};
const CHANNEL_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp',
  web: 'Web',
  email: 'E-mail',
  telegram: 'Telegram',
  instagram: 'Instagram',
};

/** Formata segundos em "Xm Ys" / "Ys" para leitura executiva. */
function fmtDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m <= 0) return `${s}s`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

/**
 * Data/hora completa em horário de Brasília (UTC-3), a partir do ISO em UTC.
 * `generated_at` vem em UTC (sem Z); parseUTC normaliza e o Intl converte para
 * America/Sao_Paulo de forma correta (sem off-by-3h).
 */
function fmtDateTimeBR(date: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parseUTC(date));
}

/** Eixo X da evolução: dia/mês em BRT (a data já é meia-noite BRT em UTC). */
function fmtDayLabel(date: string): string {
  const d = parseUTC(date);
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// ─── Cartões de previsão ML (a partir de trends[]) ──────────────────────────
// Mapeia cada tipo de tendência para um cartão com ícone, cor e texto formatado.
function trendCard(t: ExecutiveTrend) {
  switch (t.tipo) {
    case 'crescimento_demanda': {
      const up = t.valor >= 0;
      return {
        key: t.tipo,
        icon: TrendingUp,
        color: up ? '#34C759' : '#FF2D55',
        bg: up ? 'bg-green-50' : 'bg-red-50',
        titulo: 'Crescimento da demanda',
        valor: `${up ? '+' : ''}${t.valor.toFixed(1)}%`,
        detalhe: t.detalhe,
      };
    }
    case 'horarios_pico':
      return {
        key: t.tipo,
        icon: Clock,
        color: '#FF9500',
        bg: 'bg-orange-50',
        titulo: 'Horário de pico',
        valor: `${String(Math.round(t.valor)).padStart(2, '0')}h`,
        detalhe: t.detalhe,
      };
    case 'previsao_volume':
      return {
        key: t.tipo,
        icon: BarChart3,
        color: '#007AFF',
        bg: 'bg-blue-50',
        titulo: 'Previsão de volume (7d)',
        valor: String(Math.round(t.valor)),
        detalhe: t.detalhe,
      };
    case 'sla_cumprido':
      return {
        key: t.tipo,
        icon: CheckCircle,
        color: '#34C759',
        bg: 'bg-green-50',
        titulo: 'SLA cumprido',
        valor: `${t.valor.toFixed(0)}%`,
        detalhe: t.detalhe,
      };
    case 'participacao_departamento':
      return {
        key: t.tipo,
        icon: Building2,
        color: '#AF52DE',
        bg: 'bg-purple-50',
        titulo: 'Participação do líder',
        valor: `${t.valor.toFixed(0)}%`,
        detalhe: t.detalhe,
      };
    default:
      return {
        key: t.tipo,
        icon: Activity,
        color: '#8E8E93',
        bg: 'bg-gray-50',
        titulo: t.tipo,
        valor: String(t.valor),
        detalhe: t.detalhe,
      };
  }
}

// ─── Banner de aviso de dados de demonstração (sempre visível na validação) ──
// Persistente e bem visível: durante a fase de validação os números podem
// conter uma população de teste, a ser removida após a homologação.
function DemoWarningBanner() {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3.5 shadow-wa-apple">
      <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-500" />
      <div className="text-sm text-amber-900">
        <p className="font-semibold">Atenção: dados de demonstração</p>
        <p className="mt-0.5 leading-relaxed text-amber-800">
          Os dados exibidos podem incluir uma{' '}
          <span className="font-semibold">população de demonstração/teste</span>, a ser removida
          após a validação.
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// ⚠️  COMPONENTE TEMPORÁRIO — CANDIDATO A REMOÇÃO  (issue #77 / PR #76)
// ----------------------------------------------------------------------------
// AÇÃO NECESSÁRIA PARA O PRÓXIMO DEV/TERMINAL QUE EDITAR ESTE ARQUIVO:
//   O controle abaixo — DemoControls (Popular/Remover dados de teste) — é uma
//   ferramenta ONE-TIME, da fase de validação: popula/remove dados SINTÉTICOS
//   (source="demo_seed") só para validar o painel visualmente.
//
//   Alerte o usuário (responsável) e pergunte se deseja MANTER ou REMOVER:
//   - o componente DemoControls e sua renderização;
//   - no hook useWaExecutivo: seedDemo / clearDemo (e estados);
//   - a "Área de validação" / banner de demo, se não forem mais necessários.
//   Par no backend: POST/DELETE /dashboard/metrics/seed-demo. NÃO remova por
//   conta própria — é decisão do usuário.
// ============================================================================
// ─── Controles admin de seed/clear da população de demonstração ──────────────
interface DemoControlsProps {
  onSeed: () => void;
  onClear: () => void;
  loading: boolean;
  message: string | null;
}
function DemoControls({ onSeed, onClear, loading, message }: DemoControlsProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-wa-apple">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
        Área de validação
      </span>
      <button
        type="button"
        onClick={onSeed}
        disabled={loading}
        className="inline-flex items-center gap-1.5 rounded-xl bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-600 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : <Database size={14} />}
        Popular dados de teste
      </button>
      <button
        type="button"
        onClick={onClear}
        disabled={loading}
        className="inline-flex items-center gap-1.5 rounded-xl bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
        Remover dados de teste
      </button>
      {message && <span className="text-xs text-gray-500">{message}</span>}
    </div>
  );
}

interface WaExecutivoTabProps {
  /** Janela em dias vinda do seletor de período da página (default 30). */
  days?: number;
}

export default function WaExecutivoTab({ days = 30 }: WaExecutivoTabProps) {
  const {
    data,
    isLoading,
    error,
    refresh,
    refreshing,
    refreshError,
    seedDemo,
    clearDemo,
    demoLoading,
    demoMessage,
  } = useWaExecutivo(days);

  // Dispara o recálculo do painel no backend (renova para todos os usuários).
  const handleRefresh = () => {
    refresh();
  };

  // Handlers dos controles de demonstração (área admin/validação).
  const handleSeed = () => {
    seedDemo(30);
  };
  const handleClear = () => {
    const msg =
      'Remover TODOS os dados de demonstração (source="demo_seed") do dashboard? ' +
      'Os dados reais não são afetados. Esta ação não pode ser desfeita.';
    // eslint-disable-next-line no-alert
    const confirmado = window.confirm(msg);
    if (confirmado) {
      clearDemo();
    }
  };

  // Série de evolução para o gráfico de linha (data formatada em BRT).
  const evolucao = useMemo(
    () =>
      (data?.evolucao ?? []).map(d => ({
        dia: fmtDayLabel(d.date),
        total: d.total_conversations,
        encerradas: d.by_status?.closed ?? 0,
      })),
    [data],
  );

  // Distribuição por prioridade e por canal (gráficos).
  const priorityData = useMemo(
    () =>
      Object.entries(data?.by_priority ?? {}).map(([k, v]) => ({
        name: PRIORITY_LABELS[k] || k,
        value: v,
      })),
    [data],
  );
  const channelData = useMemo(
    () =>
      Object.entries(data?.by_channel ?? {}).map(([k, v]) => ({
        name: CHANNEL_LABELS[k] || k,
        value: v,
      })),
    [data],
  );

  // Participação % por departamento (sobre o total de conversas).
  const departments = useMemo(() => {
    const deps = data?.by_department ?? [];
    const total = deps.reduce((acc, d) => acc + d.count, 0);
    return deps.map(d => ({
      ...d,
      participacao: total > 0 ? Math.round((d.count / total) * 100) : 0,
    }));
  }, [data]);

  // ─── Estado: carregando ───────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="animate-slide-up space-y-6">
        <div className="flex h-[320px] items-center justify-center rounded-2xl bg-white shadow-wa-apple">
          <div className="flex flex-col items-center gap-3 text-gray-400">
            <Briefcase size={32} className="animate-pulse text-wa-500" />
            <p className="text-sm">Carregando painel executivo…</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Estado: sem permissão (403) ──────────────────────────────────────
  if (error === 'sem_permissao') {
    return (
      <div className="animate-slide-up flex h-[320px] flex-col items-center justify-center gap-3 rounded-2xl bg-white text-center shadow-wa-apple">
        <ShieldAlert size={36} className="text-orange-400" />
        <div>
          <p className="text-sm font-semibold text-gray-700">Acesso restrito</p>
          <p className="mt-1 text-sm text-gray-400">
            O painel executivo está disponível apenas para administradores e supervisores.
          </p>
        </div>
      </div>
    );
  }

  // ─── Estado: erro genérico ────────────────────────────────────────────
  if (error || !data) {
    return (
      <div className="animate-slide-up space-y-6">
        <DemoControls
          onSeed={handleSeed}
          onClear={handleClear}
          loading={demoLoading}
          message={demoMessage}
        />
        <div className="flex h-[320px] flex-col items-center justify-center gap-3 rounded-2xl bg-white text-center shadow-wa-apple">
          <AlertTriangle size={36} className="text-red-400" />
          <p className="text-sm text-gray-500">Não foi possível carregar o painel executivo.</p>
        </div>
      </div>
    );
  }

  const { kpis, narrative, trends, periodo } = data;
  const hasNarrative = narrative && (narrative.resumo || narrative.destaques.length > 0);
  const isFallback = narrative?.fonte === 'fallback_deterministica';

  // KPIs principais em cartões.
  const kpiCards = [
    {
      label: 'Total de conversas',
      value: kpis.total_conversations,
      icon: MessageCircle,
      color: '#007AFF',
      bg: 'bg-blue-50',
    },
    {
      label: 'Encerradas',
      value: kpis.closed_conversations,
      icon: CheckCircle,
      color: '#34C759',
      bg: 'bg-green-50',
    },
    {
      label: 'Em aberto',
      value: kpis.open_conversations,
      icon: Clock,
      color: '#FF9500',
      bg: 'bg-orange-50',
    },
    {
      label: 'Aguardando',
      value: kpis.waiting_conversations,
      icon: Clock,
      color: '#5856D6',
      bg: 'bg-indigo-50',
    },
    {
      label: 'Tempo médio de resposta',
      value: fmtDuration(kpis.avg_response_time_seconds),
      icon: Timer,
      color: '#007AFF',
      bg: 'bg-blue-50',
    },
    {
      label: 'Tempo médio de resolução',
      value: fmtDuration(kpis.avg_resolution_time_seconds),
      icon: Timer,
      color: '#AF52DE',
      bg: 'bg-purple-50',
    },
    {
      label: 'Satisfação média',
      value:
        typeof kpis.satisfaction_avg === 'number' ? `${kpis.satisfaction_avg.toFixed(1)}/5` : '—',
      icon: Star,
      color: '#FF9500',
      bg: 'bg-yellow-50',
    },
    {
      label: 'SLA estourado / em risco',
      value: `${kpis.sla_breached} / ${kpis.sla_at_risk}`,
      icon: ShieldAlert,
      color: '#FF2D55',
      bg: 'bg-red-50',
    },
  ];

  return (
    <div className="animate-slide-up space-y-6">
      {/* Aviso de dados de demonstração — só quando o período REALMENTE contém
          snapshots sintéticos (has_demo_data); some sozinho após remover a demo. */}
      {data.has_demo_data && <DemoWarningBanner />}

      {/* Controles admin: popular / remover população de demonstração */}
      <DemoControls
        onSeed={handleSeed}
        onClear={handleClear}
        loading={demoLoading}
        message={demoMessage}
      />

      {/* Cabeçalho do período + ação de atualização */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Briefcase size={16} className="text-wa-500" />
          <span>
            Visão de diretoria · <span className="font-semibold text-gray-700">{periodo}</span>
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Indicador de última atualização (generated_at em Brasília/UTC-3) */}
          <span className="text-xs text-gray-400">
            Última atualização:{' '}
            <span className="font-medium text-gray-600">{fmtDateTimeBR(data.generated_at)}</span>
          </span>

          {/* Botão de atualização — recalcula o painel para TODOS os usuários.
              Visível apenas no estado de sucesso, ou seja, para quem já tem
              acesso ao painel (admin/supervisor; não-autorizados veem o estado
              "Acesso restrito" e nunca chegam aqui). */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Recalcula os indicadores agora. Os dados são renovados para todos os usuários."
            className="inline-flex items-center gap-1.5 rounded-xl bg-wa-50 px-3 py-1.5 text-xs font-semibold text-wa-600 transition hover:bg-wa-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Atualizando…' : 'Atualizar dados'}
          </button>
        </div>
      </div>

      {/* Aviso: a atualização vale para todos / falha do refresh */}
      {refreshError ? (
        <div className="flex items-start gap-2 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-500" />
          <span>
            {refreshError === 'sem_permissao'
              ? 'Sem permissão para atualizar os dados (requer admin ou supervisor).'
              : 'Não foi possível atualizar os dados agora. Tente novamente em instantes.'}
          </span>
        </div>
      ) : (
        <p className="text-xs text-gray-400">
          Ao atualizar, os indicadores são recalculados e renovados para todos os usuários.
        </p>
      )}

      {/* Aviso quando ainda não há histórico suficiente (backfill não rodou):
          evita que KPIs zerados pareçam dados reais. */}
      {data.data_status === 'dados_insuficientes' && (
        <div className="flex items-start gap-2 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-500" />
          <span>
            Ainda não há histórico suficiente para as análises preditivas. As métricas serão
            consolidadas conforme o período acumula dados (o snapshot diário roda automaticamente).
          </span>
        </div>
      )}

      {/* === KPIs EXECUTIVOS === */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {kpiCards.map(kpi => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.label} className="rounded-2xl bg-white p-4 shadow-wa-apple">
              <div className={`mb-2 w-fit rounded-xl p-2 ${kpi.bg}`}>
                <Icon size={18} style={{ color: kpi.color }} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
              <p className="text-xs text-gray-500">{kpi.label}</p>
            </div>
          );
        })}
      </div>

      {/* === NARRATIVA DA IA === */}
      <div className="overflow-hidden rounded-2xl bg-white shadow-wa-apple">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-wa-500" />
            <h3 className="text-sm font-semibold text-gray-900">Análise executiva</h3>
          </div>
          {narrative && (
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                isFallback ? 'bg-gray-100 text-gray-500' : 'bg-blue-50 text-blue-600'
              }`}
            >
              {isFallback ? 'Resumo automático' : 'Gerado por IA'}
            </span>
          )}
        </div>

        <div className="p-5">
          {!hasNarrative ? (
            // Estado gracioso: sem narrativa de IA (null) ou dados insuficientes.
            <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
              <Lightbulb size={28} className="text-gray-300" />
              <p className="text-sm text-gray-400">
                Ainda não há dados suficientes para gerar a análise executiva deste período.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Resumo */}
              {narrative.resumo && (
                <p className="text-sm leading-relaxed text-gray-700">{narrative.resumo}</p>
              )}

              {/* Destaques (verde) */}
              {narrative.destaques.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Destaques
                  </p>
                  <ul className="space-y-1.5">
                    {narrative.destaques.map((d, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={i} className="flex items-start gap-2 text-sm text-green-700">
                        <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-green-500" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Alertas (vermelho) */}
              {narrative.alertas.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Alertas
                  </p>
                  <ul className="space-y-1.5">
                    {narrative.alertas.map((a, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                        <XCircle size={16} className="mt-0.5 shrink-0 text-red-500" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recomendações (laranja) */}
              {narrative.recomendacoes.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Recomendações
                  </p>
                  <ul className="space-y-1.5">
                    {narrative.recomendacoes.map((r, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={i} className="flex items-start gap-2 text-sm text-orange-700">
                        <Lightbulb size={16} className="mt-0.5 shrink-0 text-orange-500" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* === PREVISÕES ML (trends) === */}
      {trends.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {trends.map(t => {
            const card = trendCard(t);
            const Icon = card.icon;
            return (
              <div key={card.key} className="rounded-2xl bg-white p-4 shadow-wa-apple">
                <div className="flex items-center gap-2">
                  <div className={`rounded-xl p-2 ${card.bg}`}>
                    <Icon size={18} style={{ color: card.color }} />
                  </div>
                  <p className="text-xs font-medium text-gray-500">{card.titulo}</p>
                </div>
                <p className="mt-3 text-2xl font-bold text-gray-900">{card.valor}</p>
                {card.detalhe && <p className="mt-1 text-xs text-gray-400">{card.detalhe}</p>}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl bg-white p-5 text-center text-sm text-gray-400 shadow-wa-apple">
          Previsões indisponíveis: dados insuficientes no período.
        </div>
      )}

      {/* === EVOLUÇÃO (linha) === */}
      <div className="rounded-2xl bg-white p-5 shadow-wa-apple">
        <h3 className="mb-4 text-sm font-semibold text-gray-900">Evolução de conversas</h3>
        {evolucao.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={evolucao}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5ea" />
              <XAxis dataKey="dia" tick={{ fontSize: 12, fill: '#8E8E93' }} />
              <YAxis tick={{ fontSize: 12, fill: '#8E8E93' }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line
                type="monotone"
                dataKey="total"
                name="Total"
                stroke="#007AFF"
                strokeWidth={2.5}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="encerradas"
                name="Encerradas"
                stroke="#34C759"
                strokeWidth={2.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-[300px] items-center justify-center text-sm text-gray-400">
            Sem evolução no período
          </div>
        )}
      </div>

      {/* === DISTRIBUIÇÃO (prioridade / canal) === */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl bg-white p-5 shadow-wa-apple">
          <h3 className="mb-4 text-sm font-semibold text-gray-900">Por prioridade</h3>
          {priorityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={priorityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5ea" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#8E8E93' }} />
                <YAxis tick={{ fontSize: 12, fill: '#8E8E93' }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="value" name="Conversas" fill="#007AFF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[260px] items-center justify-center text-sm text-gray-400">
              Sem dados
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-white p-5 shadow-wa-apple">
          <h3 className="mb-4 text-sm font-semibold text-gray-900">Por canal</h3>
          {channelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={channelData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={85}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {channelData.map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[260px] items-center justify-center text-sm text-gray-400">
              Sem dados
            </div>
          )}
        </div>
      </div>

      {/* === TABELA POR DEPARTAMENTO === */}
      <div className="overflow-hidden rounded-2xl bg-white shadow-wa-apple">
        <div className="border-b border-gray-100 px-5 py-3.5">
          <h3 className="text-sm font-semibold text-gray-900">Departamentos</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200/60">
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Departamento
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Conversas
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Encerradas
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  SLA estourado
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  SLA em risco
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Participação
                </th>
              </tr>
            </thead>
            <tbody>
              {departments.map(d => (
                <tr key={d.department} className="border-b border-gray-100/60 hover:bg-gray-50/50">
                  <td className="px-5 py-3 font-medium text-gray-900">{d.department}</td>
                  <td className="px-5 py-3 text-gray-600">{d.count}</td>
                  <td className="px-5 py-3 text-gray-600">{d.closed}</td>
                  <td className="px-5 py-3 text-gray-600">
                    {d.sla_breached > 0 ? (
                      <span className="font-semibold text-red-600">{d.sla_breached}</span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-gray-600">
                    {d.sla_at_risk > 0 ? (
                      <span className="font-semibold text-orange-600">{d.sla_at_risk}</span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full bg-wa-500"
                          style={{ width: `${d.participacao}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-gray-700">{d.participacao}%</span>
                    </div>
                  </td>
                </tr>
              ))}
              {departments.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-gray-400">
                    Nenhum departamento no período
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
